import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  dismissDuplicatePair,
  getDuplicateAthletes,
  restoreDuplicatePair,
} from "../../../services/AdminService";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import DuplicateUsersMobileList from "./DuplicateUsersMobileList";
import DuplicateAthleteCompareDialog from "./DuplicateAthleteCompareDialog";
import {
  COLUMNS,
  cellColor,
  displayValue,
  getMatchingFields,
  groupKeyOf,
  hasPendingPairs,
  isPairDismissed,
  memberName,
  toPair,
  withPairDismissed,
  withPairRestored,
} from "./duplicateMatching";

const PAGE_SIZE = 25;

// Both endpoints answer 400/401 with { error: "..." }, which the service layer
// re-throws as-is; anything else falls back to the caller's message.
const pairActionError = (err, fallback) =>
  err?.error || err?.message || fallback;

// On 200 both endpoints answer { data: "..." }; the service returns that body,
// so the message sits one level in. The endpoints declare text/plain, so axios
// may hand back the raw string instead of a parsed object — accept either, and
// fall back to a client-built message when the body comes back empty.
const pairActionMessage = (result, fallback) =>
  (typeof result === "string" ? result : result?.data) || fallback;

function AdminDuplicateUsers() {
  const apiInstance = useAxiosInstance();
  const [groups, setGroups] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDismissed, setShowDismissed] = useState(false);
  const [selection, setSelection] = useState({ groupKey: null, userIds: [] });
  const [pairActionPending, setPairActionPending] = useState(false);
  const [comparePair, setComparePair] = useState(null);

  const debouncedSearch = useDebouncedValue(searchQuery, 250);

  const fetchDuplicates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getDuplicateAthletes(
        apiInstance,
        debouncedSearch,
        page,
        PAGE_SIZE,
        showDismissed,
      );
      setGroups(Array.isArray(response?.results) ? response.results : []);
      setTotalPages(response?.totalPages ?? 1);
      setTotalResults(response?.totalResults ?? 0);
    } catch {
      setError("Failed to load duplicate users.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiInstance, debouncedSearch, page, showDismissed]);

  useEffect(() => {
    fetchDuplicates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearch, showDismissed]);

  const selectedGroup = useMemo(
    () => groups.find((g) => groupKeyOf(g) === selection.groupKey) ?? null,
    [groups, selection.groupKey],
  );

  const selectedPairDismissed =
    selectedGroup && selection.userIds.length === 2
      ? isPairDismissed(selectedGroup, ...selection.userIds)
      : false;

  const toggleSelect = (group, userId) => {
    const key = groupKeyOf(group);
    setSelection((prev) => {
      if (prev.groupKey !== key) return { groupKey: key, userIds: [userId] };
      if (prev.userIds.includes(userId)) {
        return { ...prev, userIds: prev.userIds.filter((id) => id !== userId) };
      }
      if (prev.userIds.length === 2) return prev;
      return { ...prev, userIds: [...prev.userIds, userId] };
    });
  };

  // Already-dismissed pairs cannot be selected for merging: once one member of
  // such a pair is picked, its counterpart is locked until the pair is restored.
  const isSelectable = (group, userId) => {
    const key = groupKeyOf(group);
    if (selection.groupKey !== key) return true;
    if (selection.userIds.includes(userId)) return true;
    if (selection.userIds.length >= 2) return false;
    return !selection.userIds.some((selectedId) =>
      isPairDismissed(group, selectedId, userId),
    );
  };

  const selectionBlockedReason = (group, userId) => {
    if (isSelectable(group, userId)) return undefined;
    if (
      selection.groupKey === groupKeyOf(group) &&
      selection.userIds.some((id) => isPairDismissed(group, id, userId))
    ) {
      return "This pair was dismissed as not a duplicate. Restore it first.";
    }
    return "Only two accounts can be compared at a time.";
  };

  const handleCompare = () => {
    if (selection.userIds.length !== 2 || selectedPairDismissed) return;
    setComparePair(toPair(...selection.userIds));
  };

  // Called once combineAthleteAccounts succeeds: closes the compare dialog,
  // clears the now-stale selection, and refreshes the list so the merged pair
  // no longer appears.
  const handleMerged = () => {
    setComparePair(null);
    setSelection({ groupKey: null, userIds: [] });
    fetchDuplicates();
  };

  const handleDismiss = async () => {
    if (!selectedGroup || selection.userIds.length !== 2) return;
    if (selectedPairDismissed || pairActionPending) return;

    const pair = toPair(...selection.userIds);
    const groupKey = groupKeyOf(selectedGroup);
    const names = `${memberName(selectedGroup, pair.primaryId)} / ${memberName(
      selectedGroup,
      pair.secondaryId,
    )}`;

    try {
      setPairActionPending(true);
      const result = await dismissDuplicatePair(
        apiInstance,
        pair.secondaryId,
        pair.primaryId,
      );

      const nextGroups = withPairDismissed(
        groups,
        groupKey,
        pair,
        showDismissed,
      );
      if (nextGroups.length < groups.length) {
        setTotalResults((n) => Math.max(0, n - 1));
      }
      setGroups(nextGroups);
      setSelection({ groupKey: null, userIds: [] });
      toast.success(
        pairActionMessage(result, `Marked as not a duplicate: ${names}`),
        { position: "top-right" },
      );
    } catch (err) {
      toast.error(pairActionError(err, "Failed to dismiss the pair."), {
        position: "top-right",
      });
    } finally {
      setPairActionPending(false);
    }
  };

  const handleRestore = async (group, dismissedPair) => {
    if (pairActionPending) return;

    const pair = {
      primaryId: dismissedPair.higherUserId,
      secondaryId: dismissedPair.lowerUserId,
    };
    const names = `${memberName(group, pair.primaryId)} / ${memberName(
      group,
      pair.secondaryId,
    )}`;

    try {
      setPairActionPending(true);
      const result = await restoreDuplicatePair(
        apiInstance,
        pair.secondaryId,
        pair.primaryId,
      );
      setGroups((prev) => withPairRestored(prev, groupKeyOf(group), pair));
      toast.success(
        pairActionMessage(result, `Restored as a possible duplicate: ${names}`),
        { position: "top-right" },
      );
    } catch (err) {
      toast.error(pairActionError(err, "Failed to restore the pair."), {
        position: "top-right",
      });
    } finally {
      setPairActionPending(false);
    }
  };

  const startRecord = totalResults === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endRecord = Math.min(page * PAGE_SIZE, totalResults);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setPage(1);
    setSelection({ groupKey: null, userIds: [] });
  };

  const handleShowDismissedChange = (value) => {
    setShowDismissed(value);
    setPage(1);
    setSelection({ groupKey: null, userIds: [] });
  };

  const sharedProps = {
    groups,
    loading,
    error,
    searchQuery,
    onSearchChange: handleSearchChange,
    showDismissed,
    onShowDismissedChange: handleShowDismissedChange,
    selection,
    onToggleSelect: toggleSelect,
    isSelectable,
    selectionBlockedReason,
    onCompare: handleCompare,
    onDismiss: handleDismiss,
    onRestore: handleRestore,
    pairActionPending,
    page,
    totalPages,
    totalResults,
    startRecord,
    endRecord,
    onPageChange: setPage,
  };

  return (
    <div className="p-0 pb-20 bg-gray-100 h-full overflow-y-auto overflow-x-hidden lg:p-4 lg:pb-0">
      {/* Desktop / Large screen view */}
      <div className="hidden lg:block min-w-0">
        <div className="bg-white shadow-lg rounded-lg p-4">
          <h1 className="text-xl mb-3 uppercase text-gray-900">
            Duplicate Users
          </h1>

          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={showDismissed}
                onChange={(e) => handleShowDismissedChange(e.target.checked)}
                className="accent-red-500"
              />
              Show dismissed
            </label>
            <div className="relative">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
              />
              <input
                type="text"
                placeholder="Name/Email"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-red-400 w-52"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-10 text-gray-500">Loading...</div>
          ) : error ? (
            <div className="text-center py-10 text-red-500">{error}</div>
          ) : (
            <>
              {/* Scrollable table — overflow constrained here, not on the page */}
              <div className="overflow-auto w-full max-h-[70vh]">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-red-100">
                      <th className="px-3 py-2 border border-red-200 w-10" />
                      {COLUMNS.map((col) => (
                        <th
                          key={col.field}
                          className="px-3 py-2 text-left font-semibold text-gray-900 border border-red-200 whitespace-nowrap"
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  {groups.length === 0 && (
                    <tbody>
                      <tr>
                        <td
                          colSpan={COLUMNS.length + 1}
                          className="text-center py-10 text-gray-500"
                        >
                          No records found.
                        </td>
                      </tr>
                    </tbody>
                  )}

                  {groups.map((group) => {
                    const key = groupKeyOf(group);
                    const matches = getMatchingFields(group.members);
                    const isActiveGroup = selection.groupKey === key;
                    const canAct =
                      isActiveGroup && selection.userIds.length === 2;

                    return (
                      <tbody
                        key={key}
                        className="border-b-8 border-gray-100 align-top"
                      >
                        <tr className="bg-gray-100">
                          <td
                            colSpan={COLUMNS.length + 1}
                            className="px-3 py-2 border border-gray-200"
                          >
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <span className="font-semibold text-gray-900 uppercase">
                                {group.firstName} {group.lastName}
                                <span className="ml-2 font-normal normal-case text-gray-600">
                                  {group.members?.length ?? 0} accounts
                                </span>
                                {!hasPendingPairs(group) && (
                                  <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-300 text-gray-700 text-xs font-normal normal-case">
                                    Dismissed
                                  </span>
                                )}
                              </span>
                              {canAct && (
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={handleDismiss}
                                    disabled={pairActionPending}
                                    className="px-3 py-1.5 border border-gray-400 text-gray-700 bg-white rounded text-xs hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    Not a duplicate
                                  </button>
                                  <button
                                    onClick={handleCompare}
                                    className="px-3 py-1.5 bg-red-500 text-white rounded text-xs hover:bg-red-600 transition-colors"
                                  >
                                    Compare &amp; Merge
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>

                        {(group.members ?? []).map((member) => {
                          const checked =
                            isActiveGroup &&
                            selection.userIds.includes(member.userId);
                          return (
                            <tr
                              key={member.userId}
                              className="hover:bg-gray-50 transition-colors"
                            >
                              <td className="px-3 py-1 border border-gray-200 text-center">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={!isSelectable(group, member.userId)}
                                  title={selectionBlockedReason(
                                    group,
                                    member.userId,
                                  )}
                                  onChange={() =>
                                    toggleSelect(group, member.userId)
                                  }
                                  className="accent-red-500 disabled:cursor-not-allowed"
                                />
                              </td>
                              {COLUMNS.map((col) => (
                                <td
                                  key={col.field}
                                  className={`px-3 py-1 border text-gray-800 whitespace-nowrap ${cellColor(
                                    matches[col.field],
                                  )}`}
                                >
                                  {displayValue(member, col.field)}
                                </td>
                              ))}
                            </tr>
                          );
                        })}

                        {/* Marked whenever the API reports a dismissed pair,
                            not only while the "Show dismissed" toggle is on. */}
                        {(group.dismissedPairs?.length ?? 0) > 0 && (
                          <tr className="bg-gray-50">
                            <td
                              colSpan={COLUMNS.length + 1}
                              className="px-3 py-2 border border-gray-200"
                            >
                              <p className="text-xs font-semibold uppercase text-gray-600 mb-1">
                                Dismissed pairs
                              </p>
                              <ul className="space-y-1">
                                {group.dismissedPairs.map((pair) => (
                                  <li
                                    key={`${pair.lowerUserId}-${pair.higherUserId}`}
                                    className="flex items-center gap-3 text-xs text-gray-700"
                                  >
                                    <span className="italic">
                                      {memberName(group, pair.higherUserId)} (#
                                      {pair.higherUserId}) ↔{" "}
                                      {memberName(group, pair.lowerUserId)} (#
                                      {pair.lowerUserId})
                                    </span>
                                    <button
                                      onClick={() => handleRestore(group, pair)}
                                      disabled={pairActionPending}
                                      className="px-2 py-1 border border-gray-400 bg-white rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                      Restore
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    );
                  })}
                </table>
              </div>

              {/* Pagination — always shown when there are results */}
              {totalResults > 0 && (
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Showing {startRecord}–{endRecord} of {totalResults} results
                  </p>
                  <div className="flex items-center gap-1">
                    <PageButton
                      onClick={() => setPage((p) => p - 1)}
                      disabled={page === 1}
                    >
                      <FontAwesomeIcon
                        icon={faChevronLeft}
                        className="text-xs"
                      />
                    </PageButton>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(
                        (p) =>
                          p === 1 ||
                          p === totalPages ||
                          Math.abs(p - page) <= 1,
                      )
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, idx) =>
                        p === "..." ? (
                          <span
                            key={`ellipsis-${idx}`}
                            className="px-2 text-gray-400 text-sm"
                          >
                            …
                          </span>
                        ) : (
                          <PageButton
                            key={p}
                            onClick={() => setPage(p)}
                            active={p === page}
                          >
                            {p}
                          </PageButton>
                        ),
                      )}

                    <PageButton
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page === totalPages}
                    >
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="text-xs"
                      />
                    </PageButton>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile / Tablet view */}
      <div className="block lg:hidden">
        <DuplicateUsersMobileList {...sharedProps} />
      </div>

      <DuplicateAthleteCompareDialog
        open={Boolean(comparePair)}
        pair={comparePair}
        onClose={() => setComparePair(null)}
        onMerged={handleMerged}
      />
    </div>
  );
}

function PageButton({ onClick, disabled, active, children }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-8 h-8 flex items-center justify-center rounded text-sm transition-colors
        ${active ? "bg-red-500 text-white font-semibold" : ""}
        ${!active && !disabled ? "hover:bg-gray-100 text-gray-700" : ""}
        ${disabled ? "text-gray-300 cursor-not-allowed" : "cursor-pointer"}
      `}
    >
      {children}
    </button>
  );
}

export default AdminDuplicateUsers;
